import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Alert, Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import Loader from "Components/Common/Loader";
import { useGetProcurementDashboardSummaryQuery } from "slices/dashboardProject/dashboardSlice";

const currency = new Intl.NumberFormat("en-SG", {
  style: "currency",
  currency: "SGD",
  maximumFractionDigits: 0,
});

type ProjectOption = {
  value: number;
  label: string;
};

const ProcurementDashboard: React.FC = () => {
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const { data, isLoading, isError } = useGetProcurementDashboardSummaryQuery(selectedProjectIds);

  if (isLoading) {
    return <div className="text-center py-5"><Loader /></div>;
  }

  if (isError || !data) {
    return <Alert color="danger">Unable to load procurement dashboard data.</Alert>;
  }

  const projects = data.project_budgets || [];
  const sections = data.budget_sections || [];
  const openPurchaseOrders = data.open_purchase_orders || [];
  const recentPurchaseOrders = data.recent_purchase_orders || [];
  const recentPurchaseRequests = data.recent_purchase_requests || [];
  const summary = data.summary || {};
  const projectOptions: ProjectOption[] = (data.available_projects || []).map((project: any) => ({
    value: project.id,
    label: project.project_name || `Project ${project.id}`,
  }));
  const chartLabels = projects.map((project: any) => project.project_name);

  const spendChart = {
    series: [
      { name: "Allocated", data: projects.map((project: any) => project.allocated_cost) },
      { name: "Spent", data: projects.map((project: any) => project.actual_cost) },
    ],
    options: {
      chart: { type: "bar", height: 320, toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "48%" } },
      dataLabels: { enabled: false },
      colors: ["#405189", "#f06548"],
      xaxis: { categories: chartLabels },
      yaxis: { labels: { formatter: (value: number) => currency.format(value) } },
      tooltip: { y: { formatter: (value: number) => currency.format(value) } },
      legend: { position: "top" },
      grid: { borderColor: "#eef0f7" },
    },
  };

  const allocationChart = {
    series: projects.map((project: any) => project.allocated_cost),
    options: {
      chart: { type: "donut", height: 320 },
      labels: chartLabels,
      colors: ["#405189", "#0ab39c", "#f7b84b", "#299cdb", "#f06548", "#6559cc"],
      legend: { position: "bottom" },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (value: number) => currency.format(value) } },
      noData: { text: "No budget allocations available" },
    },
  };

  const metrics = [
    { label: "Active Projects", value: summary.active_project_count || 0, icon: "ri-building-2-line", color: "primary" },
    { label: "Open Purchase Orders", value: summary.open_purchase_order_count || 0, icon: "ri-file-list-3-line", color: "info" },
  ];

  return (
    <>
      <Card>
        <CardBody>
          <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
            <div className="fw-semibold text-nowrap">Projects</div>
            <div className="flex-grow-1" style={{ maxWidth: 560 }}>
              <Select
                isMulti
                isClearable
                classNamePrefix="select"
                options={projectOptions}
                value={projectOptions.filter((project) => selectedProjectIds.includes(project.value))}
                onChange={(selected: readonly ProjectOption[]) =>
                  setSelectedProjectIds(selected.map((project) => project.value))
                }
                placeholder="All assigned projects"
                noOptionsMessage={() => "No assigned projects available"}
              />
            </div>
            <span className="text-muted small">
              {selectedProjectIds.length
                ? `${selectedProjectIds.length} project${selectedProjectIds.length === 1 ? "" : "s"} selected`
                : "Showing all assigned projects"}
            </span>
          </div>
        </CardBody>
      </Card>

      <Row>
        {metrics.map((metric) => (
          <Col xl={6} md={6} key={metric.label}>
            <Card className="card-animate">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className={`avatar-sm flex-shrink-0 me-3`}>
                    <span className={`avatar-title bg-${metric.color}-subtle text-${metric.color} rounded fs-3`}>
                      <i className={metric.icon} />
                    </span>
                  </div>
                  <div>
                    <p className="text-uppercase fw-medium text-muted mb-1">{metric.label}</p>
                    <h4 className="mb-0">{metric.value}</h4>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        <Col xl={8}>
          <Card>
            <CardHeader><h5 className="card-title mb-0">Budget Allocation vs Spend</h5></CardHeader>
            <CardBody>
              <ReactApexChart options={spendChart.options as any} series={spendChart.series} type="bar" height={320} />
            </CardBody>
          </Card>
        </Col>
        <Col xl={4}>
          <Card>
            <CardHeader><h5 className="card-title mb-0">Budget Allocation by Project</h5></CardHeader>
            <CardBody>
              <ReactApexChart options={allocationChart.options as any} series={allocationChart.series} type="donut" height={320} />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader><h5 className="card-title mb-0">Project Budget Allocations</h5></CardHeader>
            <CardBody className="p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light"><tr><th>Project</th><th>Section</th><th>Allocated</th><th>Spent</th><th>Remaining</th><th style={{ minWidth: 150 }}>Utilization</th><th>Status</th></tr></thead>
                  <tbody>
                    {sections.map((section: any) => (
                      <tr key={section.id}>
                        <td>{section.project_name}</td><td>{section.section_name}</td><td>{currency.format(section.allocated_cost)}</td><td>{currency.format(section.actual_cost)}</td><td className={section.remaining_cost < 0 ? "text-danger" : "text-success"}>{currency.format(section.remaining_cost)}</td>
                        <td><div className="d-flex align-items-center gap-2"><div className="progress flex-grow-1" style={{ height: 7 }}><div className={`progress-bar bg-${section.utilization_percent > 100 ? "danger" : "success"}`} style={{ width: `${Math.min(section.utilization_percent, 100)}%` }} /></div><span className="small">{section.utilization_percent}%</span></div></td>
                        <td><span className={`badge bg-${section.status === "over_budget" ? "danger" : "success"}-subtle text-${section.status === "over_budget" ? "danger" : "success"}`}>{section.status.replace("_", " ")}</span></td>
                      </tr>
                    ))}
                    {!sections.length && <tr><td colSpan={7} className="text-center text-muted py-4">No project budget allocations found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center"><h5 className="card-title mb-0">Open Purchase Orders</h5><span className="text-muted small">Approved orders with pending or partially delivered status</span></CardHeader>
            <CardBody className="p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light"><tr><th>PO Number</th><th>Project</th><th>Supplier</th><th>Total Cost</th><th>PO Status</th><th>Delivery Status</th><th>Created By</th><th>Created</th></tr></thead>
                  <tbody>
                    {openPurchaseOrders.map((po: any) => (
                      <tr key={po.id}><td><Link to={`/purchase-order-detail/${po.po_number}/`} className="fw-medium">{po.po_number}</Link></td><td>{po.project_name}</td><td>{po.supplier_name}</td><td>{currency.format(po.total_cost)}</td><td><span className="badge bg-primary-subtle text-primary text-capitalize">{po.po_status.replace("_", " ")}</span></td><td><span className="badge bg-info-subtle text-info text-capitalize">{po.delivery_status.replace("_", " ")}</span></td><td>{po.created_by}</td><td>{new Date(po.created_at).toLocaleDateString()}</td></tr>
                    ))}
                    {!openPurchaseOrders.length && <tr><td colSpan={8} className="text-center text-muted py-4">No approved purchase orders with pending delivery found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader><h5 className="card-title mb-0">Recent Purchase Orders</h5></CardHeader>
            <CardBody className="p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light"><tr><th>PO Number</th><th>Project</th><th>Supplier</th><th>Total Cost</th><th>PO Status</th><th>Delivery Status</th><th>Created By</th><th>Created</th></tr></thead>
                  <tbody>
                    {recentPurchaseOrders.map((po: any) => (
                      <tr key={po.id}><td><Link to={`/purchase-order-detail/${po.po_number}/`} className="fw-medium">{po.po_number}</Link></td><td>{po.project_name}</td><td>{po.supplier_name}</td><td>{currency.format(po.total_cost)}</td><td><span className="badge bg-primary-subtle text-primary text-capitalize">{po.po_status.replace("_", " ")}</span></td><td><span className="badge bg-info-subtle text-info text-capitalize">{po.delivery_status.replace("_", " ")}</span></td><td>{po.created_by}</td><td>{new Date(po.created_at).toLocaleDateString()}</td></tr>
                    ))}
                    {!recentPurchaseOrders.length && <tr><td colSpan={8} className="text-center text-muted py-4">No purchase orders found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader><h5 className="card-title mb-0">Recent Purchase Requests</h5></CardHeader>
            <CardBody className="p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light"><tr><th>PR Number</th><th>Title</th><th>Project</th><th>Status</th><th>Created By</th><th>Created</th></tr></thead>
                  <tbody>
                    {recentPurchaseRequests.map((purchaseRequest: any) => (
                      <tr key={purchaseRequest.id}><td><Link to={`/purchase-request/${purchaseRequest.id}`} className="fw-medium">{purchaseRequest.pr_number}</Link></td><td>{purchaseRequest.title}</td><td>{purchaseRequest.project_name}</td><td><span className="badge bg-secondary-subtle text-secondary text-capitalize">{purchaseRequest.status}</span></td><td>{purchaseRequest.created_by}</td><td>{new Date(purchaseRequest.created_at).toLocaleDateString()}</td></tr>
                    ))}
                    {!recentPurchaseRequests.length && <tr><td colSpan={6} className="text-center text-muted py-4">No purchase requests found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ProcurementDashboard;
